/**
 * Words for screen readers.
 *
 * The game is drawn on a canvas, which screen readers can't see into. So the
 * game also describes itself in text: what's selected, what just happened,
 * and (on request) the whole board. main.ts puts these sentences into an
 * "ARIA live region": a page element whose changes screen readers read out
 * automatically.
 *
 * Everything here is plain text built from the game state, so it's easy to
 * test and to reword.
 */
import { PALETTE, swatchFor } from "./palette";
import type { Block, GameState, Source } from "./state";

/** A block in words, e.g. "red circle, count 3" (plus "linked" if it is). */
export function describeBlock(block: Block): string {
	const { name, shape } = swatchFor(block.color);
	return `${name} ${shape}, count ${block.count}${block.link === undefined ? "" : ", linked"}`;
}

/** What's at a launch source, e.g. "Column 2 of 4: blue square, count 4. 3 more below." */
export function describeSelection(state: GameState, source: Source): string {
	if (source.from === "column") {
		const column = state.columns[source.index] ?? [];
		const where = `Column ${source.index + 1} of ${state.columns.length}`;
		const front = column[0];
		if (!front) return `${where}: empty.`;
		const below = column.length - 1;
		return `${where}: ${describeBlock(front)}.${below > 0 ? ` ${below} more below.` : ""}`;
	}
	const where = `Tray slot ${source.index + 1} of ${state.tray.length}`;
	const block = state.tray[source.index];
	return block ? `${where}: ${describeBlock(block)}.` : `${where}: empty.`;
}

/**
 * The whole board in a few sentences: cells left by color, then the track,
 * tray and columns.
 */
export function summarize(state: GameState): string {
	const counts = new Map<number, number>();
	for (const cell of state.grid.cells)
		if (cell !== null) counts.set(cell, (counts.get(cell) ?? 0) + 1);
	const total = [...counts.values()].reduce((sum, n) => sum + n, 0);
	const byColor = [...counts]
		.sort((a, b) => b[1] - a[1])
		.map(([color, n]) => `${n} ${PALETTE[color]?.name ?? "unknown"}`)
		.join(", ");
	const used = state.tray.filter((b) => b !== null).length;
	const waiting = state.columns.reduce((sum, c) => sum + c.length, 0);
	return [
		`${total} ${total === 1 ? "cell" : "cells"} left${total > 0 ? `: ${byColor}` : ""}.`,
		`Track: ${state.runners.length} of ${state.maxRunners} blocks.`,
		`Tray: ${used} of ${state.tray.length} slots used.`,
		`${waiting} ${waiting === 1 ? "block" : "blocks"} in the columns.`,
	].join(" ");
}

/** How to play with the keyboard, read when a level starts. */
export const KEYBOARD_HELP =
	"Use the arrow keys to choose a block, Enter to launch it, 1 to 9 to launch a column, and S to hear the board.";

/**
 * Turns the game's event log into sentences, as `ClearEffects` and
 * `SoundEvents` do for animation and sound. Cleared cells are added up and
 * reported when the block finishes, rather than one cell at a time, so the
 * screen reader isn't flooded.
 */
export class Narrator {
	private state: GameState | null = null;
	private seen = 0;
	private cleared = 0;

	/**
	 * Sentences for everything that happened since the last call. `title`
	 * names the level, for the announcement when a new one starts.
	 */
	sync(state: GameState, title: string): string[] {
		const out: string[] = [];
		if (state !== this.state) {
			this.state = state;
			this.seen = 0;
			this.cleared = 0;
			const cells = state.grid.cells.filter((c) => c !== null).length;
			out.push(`${title}. ${cells} cells to clear. ${KEYBOARD_HELP}`);
		}

		for (const event of state.events.slice(this.seen)) {
			switch (event.type) {
				case "launch": {
					// The launched blocks are the newest ones on the track.
					const launched = state.runners.slice(-event.count);
					const names = launched.map((r) => describeBlock(r)).join(" and ");
					out.push(
						event.count > 1
							? `Launched ${event.count} linked blocks: ${names}.`
							: `Launched ${names || "a block"}.`,
					);
					break;
				}
				case "blocked":
					out.push(
						state.runners.length >= state.maxRunners
							? "Can't launch: the track is full."
							: "Can't launch yet: its linked blocks aren't all at the front.",
					);
					break;
				case "clear":
					this.cleared++;
					break;
				case "autoplay":
					out.push(
						"Every block left fits in the tray: sending them round automatically.",
					);
					break;
				case "stow":
					out.push(
						`${this.clearedSoFar()}${event.count > 1 ? "Linked blocks" : "A block"} moved to the tray.`,
					);
					break;
				case "retire":
					out.push(
						`${this.clearedSoFar()}${event.count > 1 ? `${event.count} blocks` : "A block"} used up.`,
					);
					break;
				case "end":
					out.push(
						event.status === "won"
							? `${this.clearedSoFar()}Level cleared! Press Enter to continue.`
							: `${state.lossReason === "trayFull" ? "Tray full: a block had nowhere to go." : "No moves left."} Press Enter to try again.`,
					);
					break;
			}
		}
		this.seen = state.events.length;
		return out;
	}

	/** "Cleared 3 cells, 21 left. " since the last report, or nothing if none were cleared. */
	private clearedSoFar(): string {
		if (this.cleared === 0 || !this.state) return "";
		const left = this.state.grid.cells.filter((c) => c !== null).length;
		const text = `Cleared ${this.cleared} ${this.cleared === 1 ? "cell" : "cells"}, ${left} left. `;
		this.cleared = 0;
		return text;
	}
}
